/**
 * src/pages/AssistantPayrollClaim.tsx
 * アシスタント自分専用：月次給与申告フォーム
 *
 * ステータスフロー:
 *   (なし) → draft（下書き保存）→ submitted（提出）
 *   ※ submitted 後は修正不可
 *   ※ 締め済み月は閲覧のみ
 *   ※ payroll_enabled = 0 のアカウントはサーバーが 403 を返す
 */

import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { api } from '../api/client'
import { todayJst } from '../utils/jst'
import { useIsMobile } from '../hooks/useIsMobile'
import { useAuth } from '../components/AuthGuard'
import { useAssistantMonth } from '../hooks/useAssistantMonth'
import { useNavigationGuard, LEAVE_CONFIRM_MESSAGE } from '../utils/navigationGuard'
import { normalizeImageUrl } from '../components/reportUtils'

// ----------------------------------------------------------------
// 型定義
// ----------------------------------------------------------------
type OvertimeDetail  = { date: string; minutes: number }
type PartTimeDetail  = { date: string; minutes: number; note: string }

type FormState = {
  full_days:                   string[]
  part_time_total_min:         number
  part_time_details:           PartTimeDetail[]
  overtime_total_min:          number
  overtime_details:            OvertimeDetail[]
  transport_total:             number
  transport_notes:             string
  other_total:                 number
  other_notes:                 string
  other_expense_receipt_urls:  string[]
  remarks:                     string
}

function emptyForm(): FormState {
  return {
    full_days:                  [],
    part_time_total_min:        0,
    part_time_details:          [],
    overtime_total_min:         0,
    overtime_details:           [],
    transport_total:            0,
    transport_notes:            '',
    other_total:                0,
    other_notes:                '',
    other_expense_receipt_urls: [],
    remarks:                    '',
  }
}

// ----------------------------------------------------------------
// 領収書画像
// ----------------------------------------------------------------
const RECEIPT_MAX_COUNT   = 10
const RECEIPT_MAX_SIZE_MB = 5
const RECEIPT_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']


// ----------------------------------------------------------------
// ユーティリティ
// ----------------------------------------------------------------
function fmt(n: number) { return n.toLocaleString('ja-JP') }

function timeToMinutes(t?: string | null): number | null {
  if (!t || !/^\d{1,2}:\d{2}$/.test(t)) return null
  const [h, m] = t.split(':').map(Number)
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null
  return h * 60 + m
}

function calcWorkMinutes(checkIn?: string | null, checkOut?: string | null): number {
  const start = timeToMinutes(checkIn)
  const end   = timeToMinutes(checkOut)
  if (start == null || end == null || end <= start) return 0
  return end - start
}

function isPartTimeAttendance(r: any): boolean {
  return r?.work_type === 'part_time' || Number(r?.work_minutes ?? 0) > 0
}

function getAttendanceWorkMinutes(r: any): number {
  const explicit = Number(r?.work_minutes ?? 0)
  if (explicit > 0) return explicit
  return calcWorkMinutes(r?.check_in, r?.check_out)
}

function getPartTimeNote(r: any): string {
  const inOut = r?.check_in && r?.check_out ? `${r.check_in}〜${r.check_out}` : ''
  const note = r?.notes ? String(r.notes) : ''
  return [inOut, note].filter(Boolean).join(' / ')
}

function getDow(dateStr: string): number {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(y, m - 1, d).getDay()
}

const DOW = ['日', '月', '火', '水', '木', '金', '土']

function parseJsonSafe<T>(s: string | undefined | null, fallback: T): T {
  if (!s) return fallback
  try { return JSON.parse(s) as T }
  catch { return fallback }
}

// ----------------------------------------------------------------
// ステータスバッジ
// ----------------------------------------------------------------
function StatusBadge({ status }: { status: 'draft' | 'submitted' | null }) {
  if (!status || status === 'draft') {
    return (
      <span style={{
        fontSize: 11, padding: '2px 8px', borderRadius: 4,
        background: '#fef3dc', color: 'var(--amber)',
        border: '1px solid rgba(176,122,18,0.25)', fontWeight: 600,
      }}>下書き</span>
    )
  }
  return (
    <span style={{
      fontSize: 11, padding: '2px 8px', borderRadius: 4,
      background: '#e8f4e9', color: 'var(--green)',
      border: '1px solid rgba(45,122,58,0.25)', fontWeight: 600,
    }}>✓ 提出済み</span>
  )
}

// ================================================================
// 日付選択グリッド（カレンダー形式のトグル）
// ================================================================
function DayPickerGrid({ month, selected, onChange, disabled }: {
  month:    string
  selected: Set<string>
  onChange: (dates: Set<string>) => void
  disabled: boolean
}) {
  const [y, m] = month.split('-').map(Number)
  const daysInMonth = new Date(y, m, 0).getDate()

  const allDays = Array.from({ length: daysInMonth }, (_, i) =>
    `${month}-${String(i + 1).padStart(2, '0')}`
  )

  // 月曜始まりで週分割
  const weeks: (string | null)[][] = []
  let week: (string | null)[] = []

  allDays.forEach((date, idx) => {
    if (idx === 0) {
      const dow    = getDow(date)
      const offset = dow === 0 ? 6 : dow - 1  // 月=0, 火=1, ..., 日=6
      for (let i = 0; i < offset; i++) week.push(null)
    }
    week.push(date)
    if (getDow(date) === 0 || idx === allDays.length - 1) {
      weeks.push(week); week = []
    }
  })
  if (week.length > 0) weeks.push(week)

  const toggle = (date: string) => {
    if (disabled) return
    const next = new Set(selected)
    if (next.has(date)) next.delete(date)
    else next.add(date)
    onChange(next)
  }

  return (
    <div>
      {/* 曜日ヘッダー（月〜日） */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, marginBottom: 4 }}>
        {['月', '火', '水', '木', '金', '土', '日'].map((d, i) => (
          <div key={d} style={{
            textAlign: 'center', fontSize: 10, fontWeight: 600,
            color: i === 5 ? '#2980b9' : i === 6 ? '#c0392b' : 'var(--ink-3)',
            paddingBottom: 3,
          }}>{d}</div>
        ))}
      </div>

      {/* 日付セル */}
      {weeks.map((wk, wi) => (
        <div key={wi} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, marginBottom: 3 }}>
          {[...wk, ...Array(Math.max(0, 7 - wk.length)).fill(null)].map((date, di) => {
            if (!date) return <div key={di} />
            const dow    = getDow(date)
            const isSun  = dow === 0
            const isSat  = dow === 6
            const isSel  = selected.has(date)
            const day    = Number(date.slice(8))

            let bg     = isSun ? '#fff5f5' : isSat ? '#f0f6ff' : 'var(--paper-2)'
            let color  = isSun ? '#c0392b' : isSat ? '#2980b9' : 'var(--ink-3)'
            let border = `1px solid ${isSun ? '#f5c6c2' : isSat ? '#bfd4f0' : 'transparent'}`

            if (isSel) { bg = 'var(--green)'; color = '#fff'; border = '1px solid transparent' }

            return (
              <button key={date} onClick={() => toggle(date)}
                disabled={disabled}
                style={{
                  textAlign: 'center', borderRadius: 5, padding: '6px 2px',
                  background: bg, color, border,
                  fontSize: 13, fontWeight: isSel ? 700 : 400,
                  fontFamily: 'var(--font-mono)', cursor: disabled ? 'default' : 'pointer',
                  transition: 'background 0.1s',
                }}>
                {day}
              </button>
            )
          })}
        </div>
      ))}

      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 6 }}>
        {disabled ? '確定済みの日付です' : '出勤日をタップ/クリックして選択してください'}
      </div>
    </div>
  )
}

// ================================================================
// フルタイム以外の勤務内訳行
// ================================================================
function PartTimeDetailRow({ detail, month, onChange, onRemove, disabled }: {
  detail:   PartTimeDetail
  month:    string
  onChange: (d: PartTimeDetail) => void
  onRemove: () => void
  disabled: boolean
}) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
      <input type="date" value={detail.date}
        min={`${month}-01`} max={`${month}-31`}
        onChange={(e) => onChange({ ...detail, date: e.target.value })}
        disabled={disabled} style={{ width: 145, fontSize: 14 }} />
      <input type="number" value={detail.minutes || ''}
        onChange={(e) => onChange({ ...detail, minutes: Number(e.target.value) || 0 })}
        min="0" step="30" disabled={disabled}
        placeholder="分" style={{ width: 72, fontSize: 14 }} />
      <span style={{ fontSize: 11, color: 'var(--ink-3)', minWidth: 44 }}>
        {detail.minutes > 0 ? `(${(detail.minutes / 60).toFixed(1)}h)` : ''}
      </span>
      <input type="text" value={detail.note}
        onChange={(e) => onChange({ ...detail, note: e.target.value })}
        disabled={disabled}
        placeholder="備考（早退・時短など）" style={{ flex: 1, minWidth: 100, fontSize: 13 }} />
      {!disabled && (
        <button onClick={onRemove}
          style={{ background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--red)', fontSize: 12, padding: '2px 4px' }}>✕</button>
      )}
    </div>
  )
}

// ================================================================
// 残業内訳行
// ================================================================
function OvertimeDetailRow({ detail, month, onChange, onRemove, disabled }: {
  detail:   OvertimeDetail
  month:    string
  onChange: (d: OvertimeDetail) => void
  onRemove: () => void
  disabled: boolean
}) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
      <input type="date" value={detail.date}
        min={`${month}-01`} max={`${month}-31`}
        onChange={(e) => onChange({ ...detail, date: e.target.value })}
        disabled={disabled}
        style={{ width: 145, fontSize: 14 }} />
      <input type="number" value={detail.minutes || ''}
        onChange={(e) => onChange({ ...detail, minutes: Number(e.target.value) || 0 })}
        min="0" step="15" disabled={disabled}
        placeholder="分" style={{ width: 72, fontSize: 14 }} />
      <span style={{ fontSize: 11, color: 'var(--ink-3)', minWidth: 44 }}>
        {detail.minutes > 0 ? `(${(detail.minutes / 60).toFixed(1)}h)` : ''}
      </span>
      {!disabled && (
        <button onClick={onRemove}
          style={{ background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--red)', fontSize: 12, padding: '2px 4px' }}>✕</button>
      )}
    </div>
  )
}

// ================================================================
// セクションヘッダー
// ================================================================
function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, color: 'var(--ink-3)',
      textTransform: 'uppercase', letterSpacing: '0.08em',
      borderBottom: '1px solid var(--paper-3)',
      paddingBottom: 8, marginBottom: 14,
    }}>
      {children}
    </div>
  )
}

// ================================================================
// 提出確認ダイアログ
// ================================================================
function SubmitConfirmDialog({ month, form, onConfirm, onCancel }: {
  month:     string
  form:      FormState
  onConfirm: () => void
  onCancel:  () => void
}) {
  const detailSum   = form.overtime_details.reduce((s, d) => s + d.minutes, 0)
  const overtimeMin = form.overtime_total_min || detailSum
  const overtimeH   = (overtimeMin / 60).toFixed(1)
  const ptMin       = form.part_time_total_min
  const ptH         = (ptMin / 60).toFixed(1)

  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="modal" style={{ maxWidth: 400 }}>
        <div className="modal-header">
          <div className="modal-title">申告内容の確認</div>
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{
            background: 'var(--paper-2)', borderRadius: 6, padding: '14px 16px',
            marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 8,
          }}>
            {[
              ['対象月',               `${month.replace('-', '年')}月`],
              ['フルタイム勤務日数',   `${form.full_days.length}日`],
              ptMin > 0 ? ['フルタイム以外の勤務', `${ptH}時間`] : null,
              ['残業合計',             `${overtimeH}時間（${overtimeMin}分）`],
              ['交通費',               `¥${fmt(form.transport_total)}`],
              ['その他経費',           `¥${fmt(form.other_total)}`],
            ].filter((row): row is [string, string] => row !== null).map(([label, value]) => (
              <div key={label as string} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: 'var(--ink-2)' }}>{label}</span>
                <span style={{ fontWeight: 600 }}>{value}</span>
              </div>
            ))}
          </div>
          <div style={{
            background: '#fef3dc', border: '1px solid rgba(176,122,18,0.25)',
            borderRadius: 6, padding: '10px 14px', fontSize: 12, color: 'var(--amber)',
          }}>
            ⚠ 提出後は内容を修正できません。<br />内容を確認のうえ提出してください。
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onCancel}>戻る</button>
          <button className="btn btn-primary" style={{ background: 'var(--green)', borderColor: 'var(--green)' }}
            onClick={onConfirm}>
            この内容で提出する
          </button>
        </div>
      </div>
    </div>
  )
}

// ================================================================
// 差分計算ユーティリティ
// ================================================================
type DiffEntry = { label: string; before: string; after: string }

function computeFormDiff(orig: FormState, curr: FormState): DiffEntry[] {
  const entries: DiffEntry[] = []

  // フルタイム勤務日
  const origDays = [...orig.full_days].sort().join(',')
  const currDays = [...curr.full_days].sort().join(',')
  if (origDays !== currDays) {
    entries.push({
      label:  'フルタイム勤務日',
      before: `${orig.full_days.length}日（${[...orig.full_days].sort().map((d) => d.slice(5).replace('-', '/')).join('、') || 'なし'}）`,
      after:  `${curr.full_days.length}日（${[...curr.full_days].sort().map((d) => d.slice(5).replace('-', '/')).join('、') || 'なし'}）`,
    })
  }

  // 時間勤務
  if (orig.part_time_total_min !== curr.part_time_total_min) {
    entries.push({
      label:  '時間勤務',
      before: `${orig.part_time_total_min}分（${(orig.part_time_total_min / 60).toFixed(1)}h）`,
      after:  `${curr.part_time_total_min}分（${(curr.part_time_total_min / 60).toFixed(1)}h）`,
    })
  }

  // 残業
  if (orig.overtime_total_min !== curr.overtime_total_min) {
    entries.push({
      label:  '残業',
      before: `${orig.overtime_total_min}分（${(orig.overtime_total_min / 60).toFixed(1)}h）`,
      after:  `${curr.overtime_total_min}分（${(curr.overtime_total_min / 60).toFixed(1)}h）`,
    })
  }

  // 交通費
  if (orig.transport_total !== curr.transport_total) {
    entries.push({
      label:  '交通費合計',
      before: `¥${orig.transport_total.toLocaleString('ja-JP')}`,
      after:  `¥${curr.transport_total.toLocaleString('ja-JP')}`,
    })
  }

  // 交通費メモ
  if ((orig.transport_notes || '') !== (curr.transport_notes || '')) {
    entries.push({
      label:  '交通費内訳メモ',
      before: orig.transport_notes || '（なし）',
      after:  curr.transport_notes || '（なし）',
    })
  }

  // その他経費
  if (orig.other_total !== curr.other_total) {
    entries.push({
      label:  'その他経費',
      before: `¥${orig.other_total.toLocaleString('ja-JP')}`,
      after:  `¥${curr.other_total.toLocaleString('ja-JP')}`,
    })
  }

  // 備考
  if ((orig.remarks || '') !== (curr.remarks || '')) {
    entries.push({
      label:  '備考',
      before: orig.remarks || '（なし）',
      after:  curr.remarks || '（なし）',
    })
  }

  return entries
}

// ================================================================
// 修正依頼フォームダイアログ（申告内容全体を編集できる形式）
// ================================================================
function CorrectionRequestDialog({ month, claim, onClose, onSubmitted }: {
  month:       string
  claim:       any
  onClose:     () => void
  onSubmitted: () => void
}) {
  // 提出済みデータを初期値として設定
  const initialForm = useMemo((): FormState => ({
    full_days:                  parseJsonSafe(claim.full_days, []),
    part_time_total_min:        claim.part_time_total_min ?? 0,
    part_time_details:          parseJsonSafe(claim.part_time_details, []),
    overtime_total_min:         claim.overtime_total_min ?? 0,
    overtime_details:           parseJsonSafe(claim.overtime_details, []),
    transport_total:            claim.transport_total ?? 0,
    transport_notes:            claim.transport_notes ?? '',
    other_total:                claim.other_total ?? 0,
    other_notes:                claim.other_notes ?? '',
    other_expense_receipt_urls: parseJsonSafe(claim.other_expense_receipt_urls, []),
    remarks:                    claim.remarks ?? '',
  }), [claim])

  const [form,        setForm]        = useState<FormState>(() => ({ ...initialForm }))
  const [selectedDays, setSelectedDays] = useState<Set<string>>(new Set(initialForm.full_days))
  const [reason,      setReason]      = useState('')
  const [submitting,  setSubmitting]  = useState(false)
  const [error,       setError]       = useState<string | null>(null)

  const setField = <K extends keyof FormState>(key: K, val: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: val }))

  const handleDaysChange = (days: Set<string>) => {
    setSelectedDays(days)
    setForm((f) => ({ ...f, full_days: Array.from(days).sort() }))
  }

  const addOTRow = () => setField('overtime_details', [...form.overtime_details, { date: `${month}-01`, minutes: 0 }])
  const updateOTRow = (i: number, d: OvertimeDetail) => {
    const next = form.overtime_details.map((r, idx) => idx === i ? d : r)
    setForm((f) => ({ ...f, overtime_details: next, overtime_total_min: next.reduce((s, r) => s + r.minutes, 0) }))
  }
  const removeOTRow = (i: number) => {
    const next = form.overtime_details.filter((_, idx) => idx !== i)
    setForm((f) => ({ ...f, overtime_details: next, overtime_total_min: next.reduce((s, r) => s + r.minutes, 0) }))
  }

  const addPTRow = () => setField('part_time_details', [...form.part_time_details, { date: `${month}-01`, minutes: 0, note: '' }])
  const updatePTRow = (i: number, d: PartTimeDetail) => {
    const next = form.part_time_details.map((r, idx) => idx === i ? d : r)
    setForm((f) => ({ ...f, part_time_details: next, part_time_total_min: next.reduce((s, r) => s + r.minutes, 0) }))
  }
  const removePTRow = (i: number) => {
    const next = form.part_time_details.filter((_, idx) => idx !== i)
    setForm((f) => ({ ...f, part_time_details: next, part_time_total_min: next.reduce((s, r) => s + r.minutes, 0) }))
  }

  // 差分を計算
  const diffEntries = useMemo(() => computeFormDiff(initialForm, form), [initialForm, form])
  const hasDiff = diffEntries.length > 0

  const handleSubmit = async () => {
    if (!reason.trim()) { setError('修正理由を入力してください'); return }
    if (!hasDiff) { setError('変更された項目がありません。修正したい項目を変更してから送信してください。'); return }
    setSubmitting(true); setError(null)
    try {
      await api.my.payrollCorrectionRequests.submit({
        target_month:      month,
        reason:            reason.trim(),
        original_payload:  initialForm,
        requested_payload: form,
        diff_payload:      diffEntries,
      })
      onSubmitted()
    } catch (ex: any) {
      setError(ex.message ?? 'エラーが発生しました')
    } finally { setSubmitting(false) }
  }

  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header" style={{ position: 'sticky', top: 0, background: 'var(--paper)', zIndex: 1 }}>
          <div className="modal-title">修正依頼を送る</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* 説明 */}
          <div style={{
            background: '#fef3dc', border: '1px solid rgba(176,122,18,0.25)',
            borderRadius: 6, padding: '10px 14px', fontSize: 12, color: 'var(--amber)',
          }}>
            ⚠ 提出済みの給与申告内容を修正できます。
          </div>

          {/* 修正理由 */}
          <div className="form-group">
            <label>修正理由 <span style={{ color: 'var(--red)' }}>*</span></label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)}
              style={{ minHeight: 68, fontSize: 13 }}
              placeholder="例：5/25の交通費と勤務時間を入れ間違えていました。" />
          </div>

          {/* ── フルタイム勤務日 ── */}
          <div>
            <SectionTitle>フルタイム勤務日（{form.full_days.length}日）</SectionTitle>
            <DayPickerGrid month={month} selected={selectedDays}
              onChange={handleDaysChange} disabled={false} />
          </div>

          {/* ── 時間勤務 ── */}
          <div>
            <SectionTitle>時間勤務・時短勤務</SectionTitle>
            {form.part_time_details.map((d, i) => (
              <PartTimeDetailRow key={i} detail={d} month={month}
                onChange={(nd) => updatePTRow(i, nd)}
                onRemove={() => removePTRow(i)} disabled={false} />
            ))}
            <button className="btn btn-outline btn-sm" onClick={addPTRow} style={{ marginTop: 4 }}>
              ＋ 時間勤務を追加
            </button>
            {form.part_time_total_min > 0 && (
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>
                合計: {form.part_time_total_min}分（{(form.part_time_total_min / 60).toFixed(1)}h）
              </div>
            )}
          </div>

          {/* ── 残業 ── */}
          <div>
            <SectionTitle>残業</SectionTitle>
            <div className="form-group" style={{ marginBottom: 8 }}>
              <label>残業時間合計（分）</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="number" min="0" step="15"
                  value={form.overtime_total_min || ''}
                  onChange={(e) => setField('overtime_total_min', Number(e.target.value) || 0)}
                  disabled={form.overtime_details.length > 0}
                  placeholder="0" style={{ width: 100, fontSize: 14 }} />
                {form.overtime_total_min > 0 && (
                  <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                    ≈ {(form.overtime_total_min / 60).toFixed(1)}時間
                  </span>
                )}
              </div>
            </div>
            {form.overtime_details.map((d, i) => (
              <OvertimeDetailRow key={i} detail={d} month={month}
                onChange={(nd) => updateOTRow(i, nd)}
                onRemove={() => removeOTRow(i)} disabled={false} />
            ))}
            <button className="btn btn-outline btn-sm" onClick={addOTRow} style={{ marginTop: 4 }}>
              ＋ 残業日を追加
            </button>
          </div>

          {/* ── 交通費 ── */}
          <div>
            <SectionTitle>交通費</SectionTitle>
            <div className="form-group" style={{ marginBottom: 8 }}>
              <label>交通費合計（円）</label>
              <input type="number" min="0" step="10"
                value={form.transport_total || ''}
                onChange={(e) => setField('transport_total', Number(e.target.value) || 0)}
                placeholder="例：1,500" style={{ width: 130, fontSize: 14 }} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>内訳メモ（任意）</label>
              <textarea value={form.transport_notes}
                onChange={(e) => setField('transport_notes', e.target.value)}
                style={{ minHeight: 48, fontSize: 13 }}
                placeholder="例：往復460円 × 5日分 + 特急代" />
            </div>
          </div>

          {/* ── その他経費 ── */}
          <div>
            <SectionTitle>その他経費</SectionTitle>
            <div className="form-group" style={{ marginBottom: 8 }}>
              <label>その他経費合計（円）</label>
              <input type="number" min="0" step="10"
                value={form.other_total || ''}
                onChange={(e) => setField('other_total', Number(e.target.value) || 0)}
                placeholder="例：800" style={{ width: 130, fontSize: 14 }} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>内訳メモ（任意）</label>
              <textarea value={form.other_notes}
                onChange={(e) => setField('other_notes', e.target.value)}
                style={{ minHeight: 48, fontSize: 13 }}
                placeholder="経費の内訳があれば" />
            </div>
          </div>

          {/* ── 備考 ── */}
          <div>
            <SectionTitle>備考</SectionTitle>
            <textarea value={form.remarks}
              onChange={(e) => setField('remarks', e.target.value)}
              style={{ minHeight: 56, fontSize: 13 }}
              placeholder="管理者への連絡事項" />
          </div>

          {/* ── 変更内容プレビュー ── */}
          <div style={{
            background: hasDiff ? '#e8f4e9' : 'var(--paper-2)',
            border: `1px solid ${hasDiff ? 'rgba(45,122,58,0.2)' : 'var(--paper-3)'}`,
            borderRadius: 6, padding: '12px 14px',
          }}>
            <div style={{ fontWeight: 600, fontSize: 12, marginBottom: hasDiff ? 8 : 0,
              color: hasDiff ? 'var(--green)' : 'var(--ink-3)' }}>
              {hasDiff ? `変更内容プレビュー（${diffEntries.length}件）` : '変更された項目がありません'}
            </div>
            {hasDiff && diffEntries.map((entry) => (
              <div key={entry.label} style={{ marginBottom: 6, fontSize: 12 }}>
                <div style={{ fontWeight: 600, color: 'var(--ink-2)' }}>{entry.label}</div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 2 }}>
                  <span style={{ color: 'var(--red)', textDecoration: 'line-through', fontSize: 11 }}>
                    {entry.before}
                  </span>
                  <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>→</span>
                  <span style={{ color: 'var(--green)', fontWeight: 600, fontSize: 11 }}>
                    {entry.after}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {error && (
            <div style={{ color: 'var(--red)', fontSize: 12, background: 'var(--red-light)', borderRadius: 6, padding: '8px 12px' }}>
              ⚠ {error}
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ position: 'sticky', bottom: 0, background: 'var(--paper)' }}>
          <button className="btn btn-outline" onClick={onClose}>キャンセル</button>
          <button className="btn btn-primary"
            onClick={handleSubmit}
            disabled={submitting || !hasDiff}
            title={!hasDiff ? '変更がありません' : undefined}>
            {submitting ? '送信中...' : '修正依頼を送る'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ================================================================
// 提出済みの読み取り専用表示
// ================================================================
function SubmittedView({ claim }: { claim: any }) {
  const fullDays:    string[]         = parseJsonSafe(claim.full_days, [])
  const overtimeDet: OvertimeDetail[] = parseJsonSafe(claim.overtime_details, [])
  const partTimeDet: PartTimeDetail[] = parseJsonSafe(claim.part_time_details, [])
  const receiptUrls: string[]         = parseJsonSafe(claim.other_expense_receipt_urls, [])
  const detailSum   = overtimeDet.reduce((s, d) => s + d.minutes, 0)
  const overtimeMin = claim.overtime_total_min || detailSum
  const ptMin       = claim.part_time_total_min ?? 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* フルタイム勤務日 */}
      <div>
        <SectionTitle>フルタイム勤務日（{fullDays.length}日）</SectionTitle>
        {fullDays.length === 0 ? (
          <span style={{ color: 'var(--ink-3)', fontSize: 13 }}>—</span>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {[...fullDays].sort().map((date) => {
              const dow = getDow(date)
              return (
                <span key={date} style={{
                  fontFamily: 'var(--font-mono)', fontSize: 13,
                  background: 'var(--green-light)', color: 'var(--green)',
                  border: '1px solid rgba(45,122,58,0.2)', borderRadius: 4, padding: '2px 8px',
                }}>
                  {date.slice(5).replace('-', '/')}
                  <span style={{ fontSize: 10, marginLeft: 3, opacity: 0.7 }}>（{DOW[dow]}）</span>
                </span>
              )
            })}
          </div>
        )}
      </div>

      {/* フルタイム以外の勤務 */}
      <div>
        <SectionTitle>フルタイム以外の勤務</SectionTitle>
        <div style={{ fontSize: 13, marginBottom: partTimeDet.length > 0 ? 8 : 0 }}>
          合計: <strong>{(ptMin / 60).toFixed(1)}時間</strong>（{ptMin}分）
        </div>
        {partTimeDet.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {partTimeDet.map((d, i) => (
              <div key={i} style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                ・{d.date.slice(5).replace('-', '/')} {d.minutes}分（{(d.minutes / 60).toFixed(1)}h）
                {d.note && <span style={{ color: 'var(--ink-3)', marginLeft: 6 }}>— {d.note}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 残業 */}
      <div>
        <SectionTitle>残業</SectionTitle>
        <div style={{ fontSize: 13, marginBottom: overtimeDet.length > 0 ? 8 : 0 }}>
          合計: <strong>{(overtimeMin / 60).toFixed(1)}時間</strong>（{overtimeMin}分）
        </div>
        {overtimeDet.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {overtimeDet.map((d, i) => (
              <div key={i} style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                ・{d.date.slice(5).replace('-', '/')} {d.minutes}分（{(d.minutes / 60).toFixed(1)}h）
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 交通費 */}
      <div>
        <SectionTitle>交通費</SectionTitle>
        <div style={{ fontSize: 13, marginBottom: claim.transport_notes ? 6 : 0 }}>
          合計: <strong>¥{fmt(claim.transport_total)}</strong>
        </div>
        {claim.transport_notes && (
          <div style={{ fontSize: 12, color: 'var(--ink-2)', whiteSpace: 'pre-wrap' }}>
            {claim.transport_notes}
          </div>
        )}
      </div>

      {/* その他経費 */}
      <div>
        <SectionTitle>その他経費</SectionTitle>
        <div style={{ fontSize: 13, marginBottom: (claim.other_notes || receiptUrls.length > 0) ? 6 : 0 }}>
          合計: <strong>¥{fmt(claim.other_total)}</strong>
        </div>
        {claim.other_notes && (
          <div style={{ fontSize: 12, color: 'var(--ink-2)', whiteSpace: 'pre-wrap', marginBottom: receiptUrls.length > 0 ? 10 : 0 }}>
            {claim.other_notes}
          </div>
        )}
        {receiptUrls.length > 0 && (
          <div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 6 }}>
              領収書画像：{receiptUrls.length}枚
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {receiptUrls.map((url, i) => (
                <a key={i} href={normalizeImageUrl(url)} target="_blank" rel="noreferrer">
                  <img src={normalizeImageUrl(url)} alt={`領収書 ${i + 1}`}
                    style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--paper-3)' }} />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 備考 */}
      {claim.remarks && (
        <div>
          <SectionTitle>備考</SectionTitle>
          <div style={{ fontSize: 13, color: 'var(--ink-2)', whiteSpace: 'pre-wrap' }}>
            {claim.remarks}
          </div>
        </div>
      )}

      {claim.submitted_at && (
        <div style={{ fontSize: 11, color: 'var(--ink-3)', textAlign: 'right' }}>
          提出日時: {claim.submitted_at.slice(0, 16).replace('T', ' ')}
        </div>
      )}
    </div>
  )
}

// ================================================================
// メインページ
// ================================================================
export default function AssistantPayrollClaim() {
  const isMobile  = useIsMobile()
  const { user }  = useAuth()

  const [month, setMonth]      = useAssistantMonth(user?.assistant_id)
  const [form,               setForm]             = useState<FormState>(emptyForm())
  // 未保存変更の判定基準（読み込み・保存成功のたびに form と同じ内容へ更新される）
  const [baselineForm,       setBaselineForm]     = useState<FormState>(emptyForm())
  const [selectedDays,       setSelectedDays]     = useState<Set<string>>(new Set())
  const [existingClaim,      setExistingClaim]    = useState<any | null>(null)
  const [isClosed,           setIsClosed]         = useState(false)
  const [loading,            setLoading]          = useState(true)
  const [saving,             setSaving]           = useState(false)
  const [showConfirm,        setShowConfirm]      = useState(false)
  const [saveResult,         setSaveResult]       = useState<string | null>(null)
  const [accessError,        setAccessError]      = useState<string | null>(null)
  // 修正依頼
  const [showCorrection,     setShowCorrection]   = useState(false)
  const [correctionResult,   setCorrectionResult] = useState<string | null>(null)
  const [myCorrections,      setMyCorrections]    = useState<any[]>([])
  // デフォルト交通費
  const [defaultTransport,   setDefaultTransport] = useState<number>(0)
  const [editingTransport,   setEditingTransport] = useState<string>('')
  const [savingTransport,    setSavingTransport]  = useState(false)
  // 領収書画像
  const [uploadingReceipt,   setUploadingReceipt] = useState(false)
  const [receiptError,       setReceiptError]     = useState<string | null>(null)
  const [deletingReceiptUrl, setDeletingReceiptUrl] = useState<string | null>(null)
  const receiptInputRef = useRef<HTMLInputElement>(null)

  // 申告データからフォーム値を組み立てる（純粋関数。baseline 算出にも使う）
  const buildFormFromClaim = (claim: any): FormState => ({
    full_days:                  parseJsonSafe(claim.full_days, []),
    part_time_total_min:        claim.part_time_total_min ?? 0,
    part_time_details:          parseJsonSafe(claim.part_time_details, []),
    overtime_total_min:         claim.overtime_total_min ?? 0,
    overtime_details:           parseJsonSafe(claim.overtime_details, []),
    transport_total:            claim.transport_total ?? 0,
    transport_notes:            claim.transport_notes ?? '',
    other_total:                claim.other_total ?? 0,
    other_notes:                claim.other_notes ?? '',
    other_expense_receipt_urls: parseJsonSafe(claim.other_expense_receipt_urls, []),
    remarks:                    claim.remarks ?? '',
  })

  const load = useCallback(async () => {
    setLoading(true)
    setAccessError(null)
    setSaveResult(null)
    try {
      const [res, profileRes, corrRes] = await Promise.all([
        api.my.payrollClaims.get(month),
        api.my.profile.get().catch(() => null),
        api.my.payrollCorrectionRequests.list(month).catch(() => ({ requests: [] })),
      ])
      setIsClosed(res.closed)
      setExistingClaim(res.claim)
      setMyCorrections(corrRes.requests ?? [])
      if (profileRes) {
        const fee = profileRes.default_transportation_fee ?? 0
        setDefaultTransport(fee)
        setEditingTransport(String(fee))
      }
      const nextForm = res.claim ? buildFormFromClaim(res.claim) : emptyForm()
      setForm(nextForm)
      setSelectedDays(new Set(nextForm.full_days))
      setBaselineForm(nextForm)
    } catch (ex: any) {
      if (ex.message?.includes('Forbidden') || ex.message?.includes('対象外')) {
        setAccessError(ex.message)
      }
    } finally { setLoading(false) }
  }, [month])

  useEffect(() => { load() }, [load])

  // 勤怠データから自動入力
  const autoFill = async () => {
    try {
      const records = await api.my.attendance.list(month)
      if (records.length === 0) { alert('この月の勤怠記録がありません。'); return }

      const fullTimeRecords = records.filter((r: any) => !isPartTimeAttendance(r))
      const partTimeRecords = records.filter((r: any) => isPartTimeAttendance(r))

      const days = fullTimeRecords.map((r: any) => r.work_date)
      const partTimeDetails: PartTimeDetail[] = partTimeRecords
        .map((r: any) => ({
          date:    r.work_date,
          minutes: getAttendanceWorkMinutes(r),
          note:    getPartTimeNote(r),
        }))
        .filter((r) => r.date && r.minutes > 0)
      const partTimeTotal = partTimeDetails.reduce((s, r) => s + r.minutes, 0)

      const otMin = records.reduce((s: number, r: any) => s + (r.overtime_min ?? 0), 0)
      // 勤怠の交通費記録がある場合はそれを優先、なければデフォルト交通費 × 勤務日数を使用
      const recordTransport = records.reduce((s: number, r: any) => s + (r.transport_fee ?? 0), 0)
      const transport = recordTransport > 0
        ? recordTransport
        : defaultTransport * records.length
      const otDetails: OvertimeDetail[] = records
        .filter((r: any) => (r.overtime_min ?? 0) > 0)
        .map((r: any) => ({ date: r.work_date, minutes: r.overtime_min }))

      setSelectedDays(new Set(days))
      setForm((f) => ({
        ...f,
        full_days:           days,
        part_time_total_min: partTimeTotal,
        part_time_details:   partTimeDetails,
        overtime_total_min:  otMin,
        overtime_details:    otDetails,
        transport_total:     transport,
      }))
      setSaveResult('✓ 勤怠データから自動入力しました。')
    } catch (ex: any) { alert(`自動入力に失敗しました: ${ex.message}`) }
  }

  // selectedDays が変わったら form.full_days も同期
  const handleDaysChange = (days: Set<string>) => {
    setSelectedDays(days)
    setForm((f) => ({ ...f, full_days: Array.from(days).sort() }))
  }

  const setField = <K extends keyof FormState>(key: K, val: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: val }))
    setSaveResult(null)
  }

  const addOvertimeRow = () => {
    const today = todayJst()
    setField('overtime_details', [...form.overtime_details, { date: today, minutes: 0 }])
  }

  const updateOvertimeRow = (idx: number, d: OvertimeDetail) => {
    const next = form.overtime_details.map((r, i) => i === idx ? d : r)
    const sum  = next.reduce((s, r) => s + r.minutes, 0)
    setForm((f) => ({ ...f, overtime_details: next, overtime_total_min: sum }))
  }

  const removeOvertimeRow = (idx: number) => {
    const next = form.overtime_details.filter((_, i) => i !== idx)
    const sum  = next.reduce((s, r) => s + r.minutes, 0)
    setForm((f) => ({ ...f, overtime_details: next, overtime_total_min: sum }))
  }

  const addPartTimeRow = () => {
    const today = todayJst()
    setField('part_time_details', [...form.part_time_details, { date: today, minutes: 0, note: '' }])
  }

  const updatePartTimeRow = (idx: number, d: PartTimeDetail) => {
    const next = form.part_time_details.map((r, i) => i === idx ? d : r)
    const sum  = next.reduce((s, r) => s + r.minutes, 0)
    setForm((f) => ({ ...f, part_time_details: next, part_time_total_min: sum }))
  }

  const removePartTimeRow = (idx: number) => {
    const next = form.part_time_details.filter((_, i) => i !== idx)
    const sum  = next.reduce((s, r) => s + r.minutes, 0)
    setForm((f) => ({ ...f, part_time_details: next, part_time_total_min: sum }))
  }

  const doSave = async (status: 'draft' | 'submitted') => {
    setSaving(true); setSaveResult(null)
    try {
      await api.my.payrollClaims.save({
        target_month:        month,
        full_days:           form.full_days,
        part_time_total_min: form.part_time_total_min,
        part_time_details:   form.part_time_details,
        overtime_total_min:  form.overtime_total_min,
        overtime_details:    form.overtime_details,
        transport_total:     form.transport_total,
        transport_notes:     form.transport_notes || undefined,
        other_total:         form.other_total,
        other_notes:         form.other_notes || undefined,
        remarks:             form.remarks || undefined,
        status,
      })
      await load()
      setSaveResult(status === 'submitted' ? '✓ 給与申告を提出しました。' : '✓ 下書きを保存しました。')
    } catch (ex: any) {
      setSaveResult(`⚠ エラー: ${ex.message}`)
    } finally { setSaving(false) }
  }

  // ── 領収書画像 ──────────────────────────────────────────────
  // アップロード・削除はその都度サーバー側で即時保存されるため、
  // ここでは form の反映のみ行う（フォーム全体の保存は不要）。
  const handleReceiptSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    e.target.value = ''
    if (files.length === 0) return

    setReceiptError(null)

    const remaining = RECEIPT_MAX_COUNT - form.other_expense_receipt_urls.length
    if (remaining <= 0) {
      setReceiptError(`領収書画像は最大${RECEIPT_MAX_COUNT}枚までです`)
      return
    }
    const targetFiles = files.slice(0, remaining)
    if (files.length > remaining) {
      setReceiptError(`一度に追加できるのは残り${remaining}枚までです。先頭${remaining}枚のみ追加します。`)
    }

    for (const file of targetFiles) {
      if (!RECEIPT_ALLOWED_TYPES.includes(file.type)) {
        setReceiptError('JPEG/PNG/WebP/GIF のみアップロード可能です')
        continue
      }
      if (file.size > RECEIPT_MAX_SIZE_MB * 1024 * 1024) {
        setReceiptError(`ファイルサイズは${RECEIPT_MAX_SIZE_MB}MB以下にしてください`)
        continue
      }
      setUploadingReceipt(true)
      try {
        const res = await api.my.payrollClaims.uploadReceipt(month, file)
        setForm((f) => ({ ...f, other_expense_receipt_urls: res.receipt_urls }))
      } catch (ex: any) {
        setReceiptError(ex.message ?? 'アップロードに失敗しました')
      } finally {
        setUploadingReceipt(false)
      }
    }
  }

  const handleReceiptDelete = async (url: string) => {
    setReceiptError(null)
    setDeletingReceiptUrl(url)
    try {
      const res = await api.my.payrollClaims.deleteReceipt(month, url)
      setForm((f) => ({ ...f, other_expense_receipt_urls: res.receipt_urls }))
    } catch (ex: any) {
      setReceiptError(ex.message ?? '削除に失敗しました')
    } finally {
      setDeletingReceiptUrl(null)
    }
  }

  // ----------------------------------------------------------------
  // 表示条件
  // ----------------------------------------------------------------
  const isSubmitted  = existingClaim?.status === 'submitted'
  const isReadOnly   = isSubmitted || isClosed
  const hasUnsaved   = !isReadOnly
  const hasWork      = form.full_days.length > 0 || form.part_time_total_min > 0

  // 保存前の未保存変更があるか（読み込み中・提出済み・締め済みでは常に false）
  const isDirty = !loading && !isReadOnly && JSON.stringify(form) !== JSON.stringify(baselineForm)

  // 離脱確認①: ブラウザのリロード・タブ閉じ
  useEffect(() => {
    if (!isDirty) return
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  // 離脱確認②: ブラウザの戻る・進む（BrowserRouter 構成のため popstate で対応）
  useEffect(() => {
    if (!isDirty) return
    window.history.pushState(null, '', window.location.href)
    const onPopState = () => {
      if (window.confirm(LEAVE_CONFIRM_MESSAGE)) {
        window.removeEventListener('popstate', onPopState)
        window.history.back()
      } else {
        window.history.pushState(null, '', window.location.href)
      }
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [isDirty])

  // 離脱確認③: サイドバー・ヘッダー等からのアプリ内遷移（共通ナビゲーション側で参照）
  useNavigationGuard(isDirty)

  if (accessError) {
    return (
      <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>
        <div style={{ fontSize: 28, marginBottom: 12 }}>🚫</div>
        <div style={{ fontSize: 14 }}>{accessError}</div>
      </div>
    )
  }

  return (
    <div>
      {/* 月選択 + ステータス */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <input type="month" value={month}
          onChange={(e) => setMonth(e.target.value)}
          style={{ fontFamily: 'var(--font-mono)', fontSize: 14, width: 150 }} />
        <StatusBadge status={existingClaim?.status ?? null} />
        {isClosed && (
          <span style={{
            fontSize: 11, color: 'var(--amber)', background: '#fef3dc',
            border: '1px solid rgba(176,122,18,0.25)', borderRadius: 4, padding: '2px 8px',
          }}>🔒 締め済み</span>
        )}
      </div>

      {loading ? (
        <div className="loading" style={{ padding: 48 }}>LOADING...</div>
      ) : (
        <>
          {/* ── 提出済みバナー + 修正依頼 ── */}
          {isSubmitted && (
            <>
              <div style={{
                background: '#e8f4e9', border: '1px solid rgba(45,122,58,0.25)',
                borderRadius: 8, padding: '14px 18px', marginBottom: 12,
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <span style={{ fontSize: 20 }}>✓</span>
                <div>
                  <div style={{ fontWeight: 700, color: 'var(--green)', fontSize: 14 }}>
                    {month.replace('-', '年')}月分の給与申告を提出済みです
                  </div>
                  {existingClaim?.submitted_at && (
                    <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                      提出日時: {existingClaim.submitted_at.slice(0, 16).replace('T', ' ')}
                    </div>
                  )}
                </div>
              </div>

              {/* 修正依頼ブロック */}
              <div style={{
                background: 'var(--paper-2)', border: '1px solid var(--paper-3)',
                borderRadius: 8, padding: '14px 18px', marginBottom: 20,
              }}>
                <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 10 }}>
                  給与申告は送信済みです。送信後の内容は直接修正できません。<br />
                  <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>
                    間違いがある場合は、下のボタンから管理者へ修正依頼を送ってください。
                  </span>
                </div>

                {correctionResult && (
                  <div style={{
                    marginBottom: 10, borderRadius: 6, padding: '8px 12px', fontSize: 12,
                    background: '#e8f4e9', color: 'var(--green)', border: '1px solid rgba(45,122,58,0.2)',
                  }}>
                    {correctionResult}
                  </div>
                )}

                <button className="btn btn-outline btn-sm"
                  onClick={() => setShowCorrection(true)}>
                  修正依頼を送る
                </button>

                {/* 過去の修正依頼一覧 */}
                {myCorrections.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 6 }}>
                      修正依頼履歴
                    </div>
                    {myCorrections.map((req: any) => {
                      const statusMap: Record<string, [string, string]> = {
                        pending:  ['未対応', 'var(--amber)'],
                        approved: ['反映済み', 'var(--green)'],
                        rejected: ['却下', 'var(--red)'],
                        resolved: ['対応済み', 'var(--ink-3)'],
                      }
                      const [label, color] = statusMap[req.status] ?? [req.status, 'var(--ink-3)']
                      return (
                        <div key={req.id} style={{
                          fontSize: 12, color: 'var(--ink-2)', marginBottom: 6,
                          display: 'flex', gap: 8, alignItems: 'flex-start',
                        }}>
                          <span style={{ color, fontWeight: 600, minWidth: 54 }}>[{label}]</span>
                          <div>
                            <div>{req.reason}</div>
                            {req.admin_note && (
                              <div style={{ color: 'var(--ink-3)', marginTop: 2 }}>
                                管理者メモ: {req.admin_note}
                              </div>
                            )}
                            <div style={{ color: 'var(--ink-3)', fontSize: 11, marginTop: 1 }}>
                              {req.created_at?.slice(0, 16).replace('T', ' ')}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── 締め済みバナー ── */}
          {isClosed && !isSubmitted && (
            <div style={{
              background: '#fef3dc', border: '1px solid rgba(176,122,18,0.25)',
              borderRadius: 8, padding: '12px 16px', marginBottom: 20,
              fontSize: 13, color: 'var(--amber)',
            }}>
              ⚠ この月は締め済みのため新規申告・修正はできません。
            </div>
          )}

          {/* ── 自動入力ボタン（draft かつ未締め） ── */}
          {!isReadOnly && (
            <div style={{ marginBottom: 16 }}>
              <button className="btn btn-outline btn-sm" onClick={autoFill}>
                📋 勤怠データから自動入力
              </button>
              <span style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 10 }}>
                登録済みの勤怠記録をもとに初期値を入力します
              </span>
            </div>
          )}

          {/* ── 保存結果メッセージ ── */}
          {saveResult && (
            <div style={{
              borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 13,
              background: saveResult.startsWith('⚠') ? '#fde8e6' : '#e8f4e9',
              color:      saveResult.startsWith('⚠') ? 'var(--red)' : 'var(--green)',
              border:     `1px solid ${saveResult.startsWith('⚠') ? 'rgba(192,57,43,0.25)' : 'rgba(45,122,58,0.2)'}`,
            }}>
              {saveResult}
            </div>
          )}

          {/* ── 提出済みは読み取り専用ビュー ── */}
          {isSubmitted ? (
            <div className="card">
              <div className="card-header">
                <span className="card-title">申告内容 — {month}</span>
              </div>
              <div style={{ padding: isMobile ? '16px 14px' : '20px 24px' }}>
                <SubmittedView claim={existingClaim} />
              </div>
            </div>
          ) : (
            // ── 編集フォーム ──
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

              {/* 1. フルタイム勤務日 */}
              <div className="card">
                <div className="card-header">
                  <span className="card-title">フルタイム勤務日</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--green)' }}>
                    {selectedDays.size}日
                  </span>
                </div>
                <div style={{ padding: isMobile ? '14px' : '16px 24px' }}>
                  <DayPickerGrid
                    month={month}
                    selected={selectedDays}
                    onChange={handleDaysChange}
                    disabled={isReadOnly}
                  />
                </div>
              </div>

              {/* 2. フルタイム以外の勤務 */}
              <div className="card">
                <div className="card-header">
                  <span className="card-title">フルタイム以外の勤務</span>
                  {form.part_time_total_min > 0 && (
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--blue)' }}>
                      {(form.part_time_total_min / 60).toFixed(1)}時間
                    </span>
                  )}
                </div>
                <div style={{ padding: isMobile ? '14px' : '16px 24px' }}>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 10 }}>
                    早退・時短・一部時間だけの勤務を記入してください。
                  </div>
                  {form.part_time_details.map((d, i) => (
                    <PartTimeDetailRow key={i} detail={d} month={month}
                      onChange={(nd) => updatePartTimeRow(i, nd)}
                      onRemove={() => removePartTimeRow(i)}
                      disabled={isReadOnly} />
                  ))}
                  {!isReadOnly && (
                    <button className="btn btn-outline btn-sm" onClick={addPartTimeRow}
                      style={{ marginTop: 4 }}>
                      ＋ 時間勤務を追加
                    </button>
                  )}
                  {form.part_time_details.length === 0 && (
                    <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                      {isReadOnly ? 'フルタイム以外の勤務なし' : '「＋ 時間勤務を追加」で記入できます'}
                    </div>
                  )}
                </div>
              </div>

              {/* 3. 残業 */}
              <div className="card">
                <div className="card-header">
                  <span className="card-title">残業</span>
                </div>
                <div style={{ padding: isMobile ? '14px' : '16px 24px' }}>
                  <div className="form-group" style={{ marginBottom: 14 }}>
                    <label>
                      残業時間合計（分）
                      {form.overtime_details.length > 0 && (
                        <span style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 6, fontWeight: 400 }}>
                          ※ 内訳を追加すると自動計算されます
                        </span>
                      )}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input type="number" min="0" step="15"
                        value={form.overtime_total_min || ''}
                        onChange={(e) => setField('overtime_total_min', Number(e.target.value) || 0)}
                        disabled={isReadOnly || form.overtime_details.length > 0}
                        placeholder="0" style={{ width: 100, fontSize: 16 }} />
                      {form.overtime_total_min > 0 && (
                        <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                          ≈ {(form.overtime_total_min / 60).toFixed(1)}時間
                        </span>
                      )}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>残業内訳（任意）</div>
                    {form.overtime_details.map((d, i) => (
                      <OvertimeDetailRow key={i} detail={d} month={month}
                        onChange={(nd) => updateOvertimeRow(i, nd)}
                        onRemove={() => removeOvertimeRow(i)}
                        disabled={isReadOnly} />
                    ))}
                    {!isReadOnly && (
                      <button className="btn btn-outline btn-sm" onClick={addOvertimeRow}
                        style={{ marginTop: 4 }}>
                        ＋ 残業日を追加
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* 4. 交通費 */}
              <div className="card">
                <div className="card-header">
                  <span className="card-title">交通費</span>
                </div>
                <div style={{ padding: isMobile ? '14px' : '16px 24px' }}>
                  {/* デフォルト交通費設定 */}
                  <div style={{
                    background: 'var(--paper-2)', borderRadius: 6, padding: '10px 14px',
                    marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                  }}>
                    <span style={{ fontSize: 12, color: 'var(--ink-3)', minWidth: 160 }}>
                      通常の交通費（日額）：
                    </span>
                    <input type="number" min="0" step="10" value={editingTransport}
                      onChange={(e) => setEditingTransport(e.target.value)}
                      style={{ width: 90, fontSize: 14 }} />
                    <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>円</span>
                    <button className="btn btn-outline btn-sm"
                      disabled={savingTransport}
                      onClick={async () => {
                        setSavingTransport(true)
                        try {
                          const fee = Math.max(0, Number(editingTransport) || 0)
                          await api.my.profile.update({ default_transportation_fee: fee })
                          setDefaultTransport(fee)
                        } catch { /* ignore */ } finally { setSavingTransport(false) }
                      }}>
                      {savingTransport ? '…' : '保存'}
                    </button>
                    <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                      ※ 勤務日追加時に自動入力されます
                    </span>
                  </div>

                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label>交通費合計（円）</label>
                    <input type="number" min="0" step="10"
                      value={form.transport_total || ''}
                      onChange={(e) => setField('transport_total', Number(e.target.value) || 0)}
                      disabled={isReadOnly} placeholder="0"
                      style={{ width: 120, fontSize: 16 }} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>内訳メモ（任意）</label>
                    <textarea value={form.transport_notes}
                      onChange={(e) => setField('transport_notes', e.target.value)}
                      disabled={isReadOnly} style={{ minHeight: 60, fontSize: 14 }}
                      placeholder="例: 東浦和〜北朝霞 往復460円 × 8日分" />
                  </div>
                </div>
              </div>

              {/* 5. その他経費 */}
              <div className="card">
                <div className="card-header">
                  <span className="card-title">その他経費</span>
                </div>
                <div style={{ padding: isMobile ? '14px' : '16px 24px' }}>
                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label>その他経費合計（円）</label>
                    <input type="number" min="0" step="10"
                      value={form.other_total || ''}
                      onChange={(e) => setField('other_total', Number(e.target.value) || 0)}
                      disabled={isReadOnly} placeholder="0"
                      style={{ width: 120, fontSize: 16 }} />
                  </div>
                  <div className="form-group" style={{ marginBottom: isReadOnly ? 0 : 16 }}>
                    <label>内訳メモ（任意）</label>
                    <textarea value={form.other_notes}
                      onChange={(e) => setField('other_notes', e.target.value)}
                      disabled={isReadOnly} style={{ minHeight: 56, fontSize: 14 }}
                      placeholder="経費の内訳があれば記載してください" />
                  </div>

                  {/* 領収書画像 */}
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>領収書画像（任意 / 複数可）</label>
                    <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 10 }}>
                      その他経費の領収書がある場合は、写真を撮影するか画像を選択して添付してください。
                    </div>

                    {!isReadOnly && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                        <input
                          ref={receiptInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          multiple
                          onChange={handleReceiptSelect}
                          style={{ display: 'none' }}
                        />
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          onClick={() => receiptInputRef.current?.click()}
                          disabled={uploadingReceipt || form.other_expense_receipt_urls.length >= RECEIPT_MAX_COUNT}
                        >
                          {uploadingReceipt ? 'アップロード中...' : '＋ 画像を追加'}
                        </button>
                        <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                          {form.other_expense_receipt_urls.length}/{RECEIPT_MAX_COUNT}枚
                        </span>
                      </div>
                    )}

                    {receiptError && (
                      <div style={{
                        color: 'var(--red)', fontSize: 12, background: '#fde8e6',
                        borderRadius: 6, padding: '6px 10px', marginBottom: 10,
                      }}>
                        ⚠ {receiptError}
                      </div>
                    )}

                    {form.other_expense_receipt_urls.length === 0 ? (
                      <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>領収書画像は未添付です</div>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                        {form.other_expense_receipt_urls.map((url) => (
                          <div key={url} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, width: 88 }}>
                            <a href={normalizeImageUrl(url)} target="_blank" rel="noreferrer">
                              <img src={normalizeImageUrl(url)} alt="領収書"
                                style={{
                                  width: 88, height: 88, objectFit: 'cover', borderRadius: 8,
                                  border: '1px solid var(--paper-3)', display: 'block',
                                }} />
                            </a>
                            {!isReadOnly && (
                              <button
                                type="button"
                                className="btn btn-outline btn-sm"
                                onClick={() => handleReceiptDelete(url)}
                                disabled={deletingReceiptUrl === url}
                                style={{
                                  fontSize: 11, padding: '5px 0', width: '100%',
                                  color: 'var(--red)', borderColor: 'rgba(192,57,43,0.3)',
                                }}
                              >
                                {deletingReceiptUrl === url ? '削除中...' : '削除'}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 6. 備考 */}
              <div className="card">
                <div className="card-header">
                  <span className="card-title">備考</span>
                </div>
                <div style={{ padding: isMobile ? '14px' : '16px 24px' }}>
                  <textarea value={form.remarks}
                    onChange={(e) => setField('remarks', e.target.value)}
                    disabled={isReadOnly} style={{ minHeight: 68, fontSize: 14 }}
                    placeholder="管理者への連絡事項があれば記載してください" />
                </div>
              </div>

              {/* ── アクションボタン ── */}
              {hasUnsaved && (
                <div style={{
                  display: 'flex', gap: 10, justifyContent: 'flex-end',
                  padding: '12px 0', flexWrap: 'wrap',
                }}>
                  <button className="btn btn-outline"
                    onClick={() => doSave('draft')}
                    disabled={saving}>
                    {saving ? '保存中...' : '下書き保存'}
                  </button>
                  <button className="btn btn-primary"
                    style={{ background: 'var(--green)', borderColor: 'var(--green)' }}
                    onClick={() => setShowConfirm(true)}
                    disabled={saving || !hasWork}>
                    申告を提出
                  </button>
                </div>
              )}

              {!hasWork && hasUnsaved && (
                <div style={{ fontSize: 11, color: 'var(--ink-3)', textAlign: 'right' }}>
                  ※ フルタイム勤務日またはフルタイム以外の勤務を入力してから提出できます
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* 提出確認ダイアログ */}
      {showConfirm && (
        <SubmitConfirmDialog
          month={month}
          form={form}
          onConfirm={() => { setShowConfirm(false); doSave('submitted') }}
          onCancel={() => setShowConfirm(false)}
        />
      )}

      {/* 修正依頼ダイアログ */}
      {showCorrection && existingClaim && (
        <CorrectionRequestDialog
          month={month}
          claim={existingClaim}
          onClose={() => setShowCorrection(false)}
          onSubmitted={() => {
            setShowCorrection(false)
            setCorrectionResult('✓ 修正依頼を送信しました。管理者が確認します。')
            load()
          }}
        />
      )}

      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 12, paddingLeft: 2 }}>
        ※ 給与計算は管理者が申告内容と勤怠記録をもとに最終確認します。
      </div>
    </div>
  )
}